import React, { useState, useEffect } from 'react';
import { vehicleService } from '../../api/services/vehicleService';
import { Question, Answer } from '../../../../shared/types';
import toast from 'react-hot-toast';
import { Loader2, X } from 'lucide-react';

interface PendingQA {
  localId: number;
  QuestionId: number;
  AnswerId: number;
  Question?: string;
  Answer?: string;
}

interface DashPartForm {
  partNumber: string;
  PriceCode: string;
  CostCode: string;
  FitmentCode: string;
  Extras: string;
  Notes: string;
}

interface Props {
  vehicleId: number;
  onClose: () => void;
  onSaved: () => void;
}

let localIdSeq = 0;

const EMPTY_FORM: DashPartForm = {
  partNumber: '', PriceCode: 'F',
  CostCode: 'F', FitmentCode: '', Extras: '', Notes: '',
};

const CODE_OPTIONS = ['A', 'B', 'F'];

const AddDashApplicationModal: React.FC<Props> = ({ vehicleId, onClose, onSaved }) => {
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [existingPartId, setExistingPartId] = useState<number | null>(null);

  const [form, setForm]         = useState<DashPartForm>(EMPTY_FORM);
  const [pendingQAs, setPendingQAs] = useState<PendingQA[]>([]);

  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers,   setAnswers]   = useState<{ [qId: number]: Answer[] }>({});

  useEffect(() => { loadLookup(); }, []);

  const loadLookup = async () => {
    try {
      const lookup = await vehicleService.getApplicationLookupData();
      setQuestions(lookup.questions ?? []);
      setAnswers(lookup.answers    ?? {});
    } catch {
      toast.error('Failed to load lookup data');
    } finally {
      setLoading(false);
    }
  };

  const handlePartNumberBlur = async () => {
    const pn = form.partNumber.trim().toUpperCase();
    if (!pn) return;

    setLookingUp(true);
    setExistingPartId(null);
    try {
      const lookup = await vehicleService.getDashPartApplicationByPartNumber(pn);
      if (!lookup) return;

      const part = await vehicleService.getDashPartApplication(lookup.Id);
      if (!part) return;

      setExistingPartId(part.Id);
      setForm(prev => ({
        ...prev,
        PriceCode:   part.PriceCode   ?? '',
        CostCode:    part.CostCode    ?? '',
        FitmentCode: part.FitmentCode ?? '',
        Extras:      part.Extras      ?? '',
        Notes:       part.Notes       ?? '',
      }));

      if (Array.isArray(part.QuestionAnswers) && part.QuestionAnswers.length > 0) {
        setPendingQAs(part.QuestionAnswers.map((qa: any) => ({
          localId:    ++localIdSeq,
          QuestionId: qa.QuestionId,
          Question:   qa.Question,
          AnswerId:   qa.AnswerId,
          Answer:     qa.Answer,
        })));
      }

      toast.success('Existing dash cover part found — fields copied');
    } catch {
      // not found — stay blank
    } finally {
      setLookingUp(false);
    }
  };

  const handlePartNumberChange = (value: string) => {
    setForm(p => ({ ...p, partNumber: value.toUpperCase() }));
    if (existingPartId !== null) {
      setExistingPartId(null);
      setForm({ ...EMPTY_FORM, partNumber: value.toUpperCase() });
      setPendingQAs([]);
    }
  };

  const handleSave = async () => {
    if (!form.partNumber.trim()) {
      toast.error('Part # is required');
      return;
    }
    try {
      setSaving(true);

      if (existingPartId !== null) {
        await vehicleService.addDashApplication(vehicleId, form.partNumber.trim());
      } else {
        const { partId } = await vehicleService.createDashPartApplicationWithLink(vehicleId, {
          partNumber:  form.partNumber.trim().toUpperCase(),
          priceCode:   form.PriceCode,
          costCode:    form.CostCode,
          fitmentCode: form.FitmentCode,
          extras:      form.Extras,
          notes:       form.Notes,
        });

        for (const qa of pendingQAs) {
          await vehicleService.addDashPartApplicationQuestion(partId, qa.QuestionId, qa.AnswerId);
        }
      }

      toast.success(existingPartId !== null ? 'Dash cover part linked to vehicle' : 'Dash cover part application created');
      onSaved();
    } catch {
      // toast shown by interceptor
    } finally {
      setSaving(false);
    }
  };

  const handleAddQA = () => {
    if (questions.length === 0) return;
    const firstQ = questions[0];
    const firstA = answers[firstQ.Id]?.[0];
    if (!firstA) return;
    setPendingQAs(prev => [...prev, {
      localId:    ++localIdSeq,
      QuestionId: firstQ.Id, Question: firstQ.Name,
      AnswerId:   firstA.Id, Answer:   firstA.Name,
    }]);
  };

  const handleDeleteQA = (localId: number) => {
    setPendingQAs(prev => prev.filter(q => q.localId !== localId));
  };

  const handleQAChange = (localId: number, field: 'QuestionId' | 'AnswerId', value: number) => {
    setPendingQAs(prev => prev.map(qa => {
      if (qa.localId !== localId) return qa;
      if (field === 'QuestionId') {
        const newQ   = questions.find(q => q.Id === value);
        const firstA = answers[value]?.[0];
        return {
          ...qa,
          QuestionId: value,
          Question:   newQ?.Name,
          AnswerId:   firstA?.Id   ?? qa.AnswerId,
          Answer:     firstA?.Name ?? qa.Answer,
        };
      }
      const newA = answers[qa.QuestionId]?.find(a => a.Id === value);
      return { ...qa, AnswerId: value, Answer: newA?.Name };
    }));
  };

  const sel = 'px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-y-auto pt-12 pb-8">
      <div className="bg-white dark:bg-gray-800 rounded shadow-xl w-full max-w-xl mx-4">

        {/* Header */}
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Add Dash Cover Application</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-7 h-7 animate-spin text-blue-600 dark:text-blue-400" />
          </div>
        ) : (
          <div className="px-5 py-5 space-y-4">

            {/* Row 1: Part #, Price Code, Production Code */}
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Part #</label>
                <div className="relative">
                  <input
                    type="text"
                    value={form.partNumber}
                    onChange={e => handlePartNumberChange(e.target.value)}
                    onBlur={handlePartNumberBlur}
                    maxLength={20}
                    placeholder="e.g. DV670"
                    className={`${sel} w-28 ${existingPartId !== null ? 'border-green-500 dark:border-green-500' : ''}`}
                    autoFocus
                  />
                  {lookingUp && (
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500 absolute right-2 top-1/2 -translate-y-1/2" />
                  )}
                </div>
                {existingPartId !== null && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">Existing part</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Price Code</label>
                <select value={form.PriceCode}
                  onChange={e => setForm(p => ({ ...p, PriceCode: e.target.value }))}
                  className={`${sel} w-20`}>
                  <option value=""></option>
                  {CODE_OPTIONS.map(code => <option key={code} value={code}>{code}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Production Code</label>
                <select value={form.CostCode}
                  onChange={e => setForm(p => ({ ...p, CostCode: e.target.value }))}
                  className={`${sel} w-24`}>
                  <option value=""></option>
                  {CODE_OPTIONS.map(code => <option key={code} value={code}>{code}</option>)}
                </select>
              </div>
            </div>

            {/* Dash Image (FitmentCode) */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Dash Image</label>
              <input type="text" value={form.FitmentCode}
                onChange={e => setForm(p => ({ ...p, FitmentCode: e.target.value }))}
                maxLength={10}
                className={`${sel} w-40`} />
            </div>

            {/* Extras */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Extras</label>
              <input type="text" value={form.Extras}
                onChange={e => setForm(p => ({ ...p, Extras: e.target.value }))}
                className={`${sel} w-full`} />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Notes</label>
              <textarea value={form.Notes}
                onChange={e => setForm(p => ({ ...p, Notes: e.target.value }))}
                rows={3}
                className={`${sel} w-full resize-y`} />
            </div>

            {/* Questions (editable for new part; read-only display when existing) */}
            {existingPartId === null && (
              <div>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Questions</h3>
                <div className="space-y-2">
                  {pendingQAs.map(qa => (
                    <div key={qa.localId} className="flex items-center gap-2">
                      <button
                        onClick={() => handleDeleteQA(qa.localId)}
                        className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 shrink-0"
                      >
                        Delete
                      </button>
                      <select value={qa.QuestionId}
                        onChange={e => handleQAChange(qa.localId, 'QuestionId', parseInt(e.target.value))}
                        className={`${sel} flex-1`}>
                        {questions.map(q => <option key={q.Id} value={q.Id}>{q.Name}</option>)}
                      </select>
                      <select value={qa.AnswerId}
                        onChange={e => handleQAChange(qa.localId, 'AnswerId', parseInt(e.target.value))}
                        className={`${sel} flex-1`}>
                        {(answers[qa.QuestionId] ?? []).map(a => <option key={a.Id} value={a.Id}>{a.Name}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                <button onClick={handleAddQA} disabled={questions.length === 0}
                  className="mt-3 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40">
                  Add Question
                </button>
              </div>
            )}

            {existingPartId !== null && pendingQAs.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Questions</h3>
                <div className="space-y-1">
                  {pendingQAs.map(qa => (
                    <div key={qa.localId} className="text-sm text-gray-700 dark:text-gray-300">
                      {qa.Question}: <span className="font-medium">{qa.Answer}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        )}

        {/* Footer */}
        {!loading && (
          <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750 rounded-b">
            <button onClick={onClose}
              className="px-4 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving || !form.partNumber.trim()}
              className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : existingPartId !== null ? 'Link Part' : 'Add Part'}
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default AddDashApplicationModal;
